// Presentation helpers for races. No modeling: everything here reads published fields.

import type { ChoiceSummary, Interval, Meta, RaceFile, RaceGroup, RaceStatus, RaceSummary, Unit } from '../types';
import { fmtProb } from './format';

export const STATUS_TEXT: Record<RaceStatus, string> = {
  uncontested: 'Uncontested',
  not_reporting: 'Not reporting',
  counting: 'Counting',
  leaning: 'Leaning',
  projected: 'Projected',
  decided: 'Decided',
  called: 'Called (AP)',
};

export const GROUP_LABEL: Record<RaceGroup, string> = {
  statewide: 'Statewide offices',
  congress: 'U.S. House',
  state_house: 'S.C. House',
  county_office: 'County offices',
  county_council: 'County Council',
  school_board: 'School Board',
  psd: 'Public Service Districts',
  soil_water: 'Soil & Water',
  referendum: 'Referendums',
  other: 'Other local races',
};
export const GROUP_ORDER: RaceGroup[] = ['statewide', 'congress', 'state_house', 'county_council', 'county_office', 'school_board', 'psd', 'soil_water', 'referendum', 'other'];

export const colorVar = (c: Pick<ChoiceSummary, 'color'> | null | undefined) => (c ? `var(--c-${c.color})` : 'var(--c-neutral)');

/**
 * Resolve `pair` to the two choices. The contract says `pair` holds choice_ids; as a defensive
 * fallback a party code ('DEM') or a name ('YES') is also matched.
 */
export function resolvePair<C extends ChoiceSummary>(race: { pair: [string, string] | null; choices: C[] }): [C, C] | null {
  if (!race.pair) return null;
  const find = (p: string) =>
    race.choices.find((c) => c.choice_id === p) ??
    race.choices.find((c) => c.party === p) ??
    race.choices.find((c) => c.name.toUpperCase() === p.toUpperCase());
  const a = find(race.pair[0]);
  const b = find(race.pair[1]);
  return a && b && a !== b ? [a, b] : null;
}

const SUFFIX = /^(jr\.?|sr\.?|ii|iii|iv|v)$/i;
/** Last name for candidates ("Pat Wilson" -> "Wilson"; tickets "Donald J Trump/JD Vance" -> "Trump"); Yes/No and write-ins as is. */
export function shortName(c: Pick<ChoiceSummary, 'name'>, kind?: RaceSummary['kind']): string {
  const n = c.name.trim();
  // referendum answers: "Yes, In favor of the Question" -> "Yes"
  if (kind === 'referendum' || /^(yes|no)\b/i.test(n)) return n.split(',')[0]!.trim() || n;
  if (/^write[- ]?ins?$/i.test(n)) return n;
  const first = c.name.split('/')[0]!.trim() || c.name.trim();
  const parts = first.replace(/,/g, '').replace(/\s*\(.*?\)\s*/g, ' ').trim().split(/\s+/);
  while (parts.length > 1 && SUFFIX.test(parts[parts.length - 1]!)) parts.pop();
  return parts[parts.length - 1] ?? c.name;
}
/** Compact display name for tight spots: tickets as "Harris/Walz", referendum answers as "Yes". */
export function compactName(c: Pick<ChoiceSummary, 'name'>, kind?: RaceSummary['kind']): string {
  if (kind === 'referendum' || /^(yes|no)\b/i.test(c.name.trim())) return shortName(c, kind);
  if (c.name.includes('/')) return c.name.split('/').map((p) => shortName({ name: p })).join('/');
  return c.name;
}
/** Short names that are unique within the given choices (falls back to the full name on collisions). */
export function uniqueShortNames(choices: Pick<ChoiceSummary, 'choice_id' | 'name'>[], kind?: RaceSummary['kind']): Map<string, string> {
  const short = choices.map((c) => [c.choice_id, shortName(c, kind)] as const);
  const count = new Map<string, number>();
  for (const [, s] of short) count.set(s, (count.get(s) ?? 0) + 1);
  return new Map(short.map(([id, s], i) => [id, (count.get(s) ?? 0) > 1 ? compactName(choices[i]!, kind) : s]));
}
const pairName = (pair: [ChoiceSummary, ChoiceSummary], side: 0 | 1, kind?: RaceSummary['kind']) => uniqueShortNames(pair, kind).get(pair[side].choice_id)!;

const PARTY_ABBR: Record<string, string> = { DEM: 'D', REP: 'R', LIB: 'L', GRN: 'G', CON: 'C', WFM: 'WF', WRK: 'W', ALI: 'A', ALN: 'A', AME: 'A', UNA: 'U', UNC: 'U', IND: 'I', PET: 'P', NON: '' };
/** One/two-letter party abbreviation; '' for none / NON (write-ins). */
export const partyAbbr = (party: string | null | undefined): string => (!party ? '' : party in PARTY_ABBR ? PARTY_ABBR[party]! : party.slice(0, 1));
export const partyTag = (c: Pick<ChoiceSummary, 'party'>) => (partyAbbr(c.party) ? ` (${partyAbbr(c.party)})` : '');

/** "Wilson +4.5" (positive margin favours pair[0]). */
export function marginText(m: number | null | undefined, pair: [ChoiceSummary, ChoiceSummary] | null, kind?: RaceSummary['kind'], digits = 1): string {
  if (m === null || m === undefined || !pair) return '—';
  const v = Math.abs(m);
  if (v < 0.05) return 'Even';
  return `${pairName(pair, m > 0 ? 0 : 1, kind)} +${v.toFixed(digits)}`;
}
/** Compact form for tables/axes: "D+4.5" for partisan pairs with distinct party letters, else "Wilson +4.5". */
export function marginShort(m: number | null | undefined, pair: [ChoiceSummary, ChoiceSummary] | null, kind?: RaceSummary['kind'], digits = 1): string {
  if (m === null || m === undefined || !pair) return '—';
  const v = Math.abs(m);
  if (v < 0.05) return 'Even';
  const c = m > 0 ? pair[0] : pair[1];
  const l0 = partyAbbr(pair[0].party), l1 = partyAbbr(pair[1].party);
  if (l0 && l1 && l0 !== l1) return `${partyAbbr(c.party)}+${v.toFixed(digits)}`;
  if (kind === 'referendum') return `${pairName(pair, m > 0 ? 0 : 1, kind)}+${v.toFixed(digits)}`;
  return `${pairName(pair, m > 0 ? 0 : 1, kind)} +${v.toFixed(digits)}`;
}
/**
 * Margin interval in words. Both ends on one side: "Trump +17.8 to +17.9" (smaller lead first); crossing zero:
 * "Wilson +5.7 to Johnson +0.9"; ends that round to the same text collapse to one ("Harris +6.1").
 */
export function marginRange(iv: Interval | null | undefined, pair: [ChoiceSummary, ChoiceSummary] | null, kind: RaceSummary['kind'], which: 50 | 90 = 90): string {
  if (!iv || !pair) return '—';
  const lo = which === 90 ? iv.lo90 : iv.lo50;
  const hi = which === 90 ? iv.hi90 : iv.hi50;
  const a = marginText(lo, pair, kind), b = marginText(hi, pair, kind);
  if (a === b) return a;
  const side = (m: number) => (Math.abs(m) < 0.05 ? 0 : m > 0 ? 1 : -1);
  if (side(lo) !== 0 && side(lo) === side(hi)) {
    const [x, y] = [Math.abs(lo), Math.abs(hi)].sort((p, q) => p - q);
    return `${pairName(pair, lo > 0 ? 0 : 1, kind)} +${x!.toFixed(1)} to +${y!.toFixed(1)}`;
  }
  return `${a} to ${b}`;
}

/** Vote-for-N margin in words: "Last seat: Platt over Levine +2.7" (pair = cutoff [N-th, N+1-th]). */
export function lastSeatText(m: number | null | undefined, pair: [ChoiceSummary, ChoiceSummary] | null, kind?: RaceSummary['kind']): string {
  if (m === null || m === undefined || !pair) return '—';
  const names = uniqueShortNames(pair, kind);
  const [w, l] = m >= 0 ? [pair[0], pair[1]] : [pair[1], pair[0]];
  if (Math.abs(m) < 0.05) return `Last seat: ${names.get(w.choice_id)} and ${names.get(l.choice_id)} even`;
  return `Last seat: ${names.get(w.choice_id)} over ${names.get(l.choice_id)} +${Math.abs(m).toFixed(1)}`;
}

/**
 * How a history point's margins line up with the race's current pair: 1 = same orientation, -1 = same two
 * choices reversed (flip the sign), null = a different pair (don't draw that point's margin). Points without
 * their own `pair` (older files) use the history file's pair.
 */
export function historyOrientation(pointPair: [string, string] | null | undefined, histPair: [string, string] | null, curPair: [string, string] | null): 1 | -1 | null {
  const pp = pointPair === undefined ? histPair : pointPair;
  if (!pp || !curPair) return null;
  if (pp[0] === curPair[0] && pp[1] === curPair[1]) return 1;
  if (pp[0] === curPair[1] && pp[1] === curPair[0]) return -1;
  return null;
}
/** Flip an interval's sign (lo/hi swap). */
export function orientInterval(iv: Interval, sign: 1 | -1): Interval {
  return sign === 1 ? iv : { median: -iv.median, lo50: -iv.hi50, hi50: -iv.lo50, lo90: -iv.hi90, hi90: -iv.lo90 };
}

/** Symmetric "nice" half-range for a margin axis. */
export function niceHalfRange(...vals: (number | null | undefined)[]): number {
  const m = Math.max(5, ...vals.filter((v): v is number => typeof v === 'number' && Number.isFinite(v)).map(Math.abs));
  for (const step of [5, 10, 15, 20, 30, 40, 50, 75, 100]) if (m <= step * 0.98) return step;
  return 100;
}

/** Label of baseline 1 / 2 (Unit.margin_b22 / margin_b24). Matched by id 'b22'/'b24' or position. */
/** 'Governor and Lieutenant Governor (2022)' → '2022 Governor'; 'U.S. House of Representatives, District 1 (2020)' →
 *  '2020 U.S. House'; 'Statewide Constitutional Amendment 1 (2022)' → '2022 Amendment 1'. */
export function shortContest(contest: string | undefined, fallback: string): string {
  if (!contest) return fallback;
  const year = /\((\d{4})\)/.exec(contest)?.[1] ?? /\b(19|20)\d{2}\b/.exec(contest)?.[0] ?? '';
  let name = contest.replace(/\s*\(\d{4}\)\s*/, ' ').trim();
  name = name.replace(/^U\.S\. House of Representatives.*$/i, 'U.S. House').replace(/^Statewide\s+/i, '')
    .replace(/^Constitutional\s+/i, '').split(/\s+and\s+|,/)[0]!.trim();
  return `${year} ${name}`.trim() || fallback;
}

export function baselineLabel(baselines: { id: string; label: string }[], which: 1 | 2, fallback: string): string {
  const b = baselines.find((x) => x.id === (which === 1 ? 'b22' : 'b24')) ?? baselines[which - 1];
  return b?.label ?? fallback;
}

export function isFrozen(raceId: string, frozen: string[] | undefined) {
  return !!frozen?.includes(raceId);
}

/** Natural sort ("Mt. Pleasant 2" before "Mt. Pleasant 10"). */
export const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });

// ------------------------------------------------------------------ v2 plain-English helpers

/** Initials for an avatar: "Kamala D Harris/Tim Walz" -> "KH", "Write-In" -> "W", "Yes, In favor…" -> "Y". */
export function initials(name: string): string {
  const first = name.split('/')[0]!.split(',')[0]!.trim();
  if (/^write[- ]?in/i.test(first)) return 'W';
  if (/^yes\b/i.test(first)) return 'Y';
  if (/^no\b/i.test(first)) return 'N';
  const parts = first.replace(/\(.*?\)/g, ' ').split(/\s+/).filter((p) => p && !SUFFIX.test(p) && !/^[A-Z]\.?$/.test(p));
  if (parts.length === 0) return first.slice(0, 1).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).replace(/^./, (c) => c.toUpperCase());
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Display name for a candidate on cards: governor/president only (no running mate). */
export function cardName(c: Pick<ChoiceSummary, 'name'>, kind?: RaceSummary['kind']): string {
  if (kind === 'referendum') return shortName(c, kind);
  return c.name.split('/')[0]!.trim();
}

// ------------------------------------------------------------------ AP calls
// The AP's calls (entered by the results desk) are authoritative: they override our own status, and the headline and
// the winner marks always follow them. The needle and estimates stay visible.

type ApFields = Pick<RaceSummary, 'status' | 'projected_winners' | 'vote_for'> & { ap_winners?: string[] | null };
/** choice_ids the AP has called. Older files have no `ap_winners` (→ []); a 'called' race without it uses projected_winners. */
export function apIds(r: ApFields): string[] {
  const ap = r.ap_winners ?? [];
  return ap.length ? ap : r.status === 'called' ? r.projected_winners : [];
}
/** The AP has called the whole race (every seat). */
export function isCalled(r: ApFields): boolean {
  const n = apIds(r).length;
  return r.status === 'called' || (n > 0 && (r.vote_for <= 1 || n >= r.vote_for));
}
/** Vote-for-N race where the AP has called some seats but not all (the model's status stands for the rest). */
export const apPartial = (r: ApFields): number => (isCalled(r) ? 0 : apIds(r).length);

export type WinKind = 'ap' | 'decided' | 'projected';
/**
 * Winner marks per choice_id: the AP's calls first; our own projection / decision only for the seats the AP hasn't
 * called, and never against it (if the model's winners plus the AP's would exceed the seats, only the AP's are marked).
 */
export function winMarks(r: ApFields): Map<string, WinKind> {
  const out = new Map<string, WinKind>();
  const ap = apIds(r);
  for (const id of ap) out.set(id, 'ap');
  if (isCalled(r) || (r.status !== 'projected' && r.status !== 'decided')) return out;
  const model = r.projected_winners.filter((id) => !ap.includes(id));
  if (ap.length + model.length > r.vote_for) return out;
  for (const id of model) out.set(id, r.status === 'decided' ? 'decided' : 'projected');
  return out;
}
export const WIN_TEXT: Record<WinKind, string> = { ap: 'Winner (AP)', decided: 'Winner', projected: 'Projected' };
export const WIN_TITLE: Record<WinKind, string> = { ap: 'Winner — called by the Associated Press', decided: 'Winner — mathematically certain', projected: 'Projected winner (our estimate)' };

/** Leader: the AP's winner, else by chance of winning (if published), else by counted votes. */
export function leader(r: Pick<RaceSummary, 'choices' | 'p_win'> & ApFields): ChoiceSummary | null {
  const ap = apIds(r);
  if (ap.length) return r.choices.find((c) => c.choice_id === ap[0]) ?? null;
  if (r.projected_winners.length) return r.choices.find((c) => c.choice_id === r.projected_winners[0]) ?? null;
  if (r.p_win) {
    const best = Object.entries(r.p_win).sort((a, b) => b[1] - a[1])[0];
    if (best) return r.choices.find((c) => c.choice_id === best[0]) ?? null;
  }
  return r.choices[0] && r.choices[0].votes > 0 ? r.choices[0] : null;
}

/** Short pill text: "No results" · "Counting" · "Tossup" · "Leans Wilson" · "Likely Wilson" · "Projected" · "Winner" · "Winner · AP" · "Unopposed". */
export function pillText(r: Pick<RaceSummary, 'status' | 'choices' | 'p_win' | 'projected_winners' | 'kind' | 'vote_for'> & { ap_winners?: string[] | null }): string {
  if (isCalled(r)) return apIds(r).length > 1 ? 'Winners · AP' : 'Winner · AP';
  switch (r.status) {
    case 'called': return 'Winner · AP';
    case 'not_reporting': return 'No results yet';
    case 'counting': return 'Counting';
    case 'uncontested': return 'Unopposed';
    case 'projected': return 'Projected';
    case 'decided': return 'Winner';
    case 'leaning': {
      if (r.vote_for > 1) {
        const sec = secureSeats(r);
        return sec.length >= r.vote_for ? 'Likely winners' : sec.length ? `${sec.length} of ${r.vote_for} seats likely` : 'Tossup';
      }
      const l = leader(r);
      const p = l && r.p_win ? r.p_win[l.choice_id] ?? 0 : 0;
      if (!l || p < 0.65) return 'Tossup';
      return `${p >= 0.9 ? 'Likely' : 'Leans'} ${shortName(l, r.kind)}`;
    }
  }
}

/** One plain sentence answering "who's ahead and how sure are we". */
export function verdict(r: Pick<RaceSummary, 'status' | 'choices' | 'p_win' | 'projected_winners' | 'kind' | 'vote_for' | 'reason_no_needle'> & { ap_winners?: string[] | null }): string {
  const names = (ids: string[]) => ids.map((id) => r.choices.find((c) => c.choice_id === id)).filter(Boolean).map((c) => cardName(c!, r.kind)).join(', ');
  const nameList = (ids: string[]) => listText(ids.map((id) => r.choices.find((c) => c.choice_id === id)).filter((c): c is ChoiceSummary => !!c).map((c) => cardName(c, r.kind)));
  const ap = apIds(r);
  if (isCalled(r)) return `${ap.length > 1 ? 'Winners' : 'Winner'}: ${nameList(ap) || '—'} — called by the Associated Press.`;
  if (ap.length) return `${nameList(ap)} ${ap.length > 1 ? 'have each' : 'has'} won a seat — called by the Associated Press. ${restVerdict(r, ap, nameList)}`.trim();
  const l = leader(r);
  switch (r.status) {
    case 'called': return 'Called by the Associated Press.';
    case 'not_reporting': return 'No votes have been counted yet.';
    case 'uncontested': {
      const who = r.choices.filter((c) => !isWriteIn(c)).map((c) => cardName(c, r.kind));
      return `${listText(who) || 'The only candidate'} ${who.length > 1 ? 'are' : 'is'} unopposed.`;
    }
    case 'counting': return `Too early to call — ${(r.reason_no_needle ?? 'not enough results yet').replace(/^./, (c) => c.toLowerCase()).replace(/\.$/, '')}.`;
    case 'decided': return `Winner: ${names(r.projected_winners) || (l ? cardName(l, r.kind) : '—')} — mathematically certain.`;
    case 'projected': return `Projected winner${r.projected_winners.length > 1 ? 's' : ''}: ${names(r.projected_winners)}.`;
    case 'leaning': {
      if (r.vote_for > 1) {
        // seat-based, consistent with the pill
        const sec = secureSeats(r);
        const left = r.vote_for - sec.length;
        const nm = listText(sec.map((c) => cardName(c, r.kind)));
        if (sec.length >= r.vote_for) return `Likely winners: ${nm}.`;
        if (!sec.length) return 'Too close to call — no candidate is clearly ahead for a seat.';
        return `${nm} ${sec.length > 1 ? 'are' : 'is'} likely to win a seat; the last ${left === 1 ? 'seat is' : `${left} seats are`} too close to call.`;
      }
      if (!l) return 'Too close to call.';
      const p = r.p_win?.[l.choice_id];
      if (p === undefined || p < 0.65) return 'Too close to call.';
      const who = r.kind === 'referendum' ? `“${shortName(l, r.kind)}” is` : `${cardName(l, r.kind)} is`;
      return `${who} ${p >= 0.9 ? 'very likely' : 'favored'} to win — ${fmtProb(p)} chance.`;
    }
  }
}

/** Vote-for-N with some seats called by the AP: the model's verdict for the remaining seats only. */
function restVerdict(r: Pick<RaceSummary, 'status' | 'choices' | 'p_win' | 'projected_winners' | 'vote_for'>, ap: string[], nameList: (ids: string[]) => string): string {
  const left = r.vote_for - ap.length;
  const seats = left === 1 ? 'the last seat' : `the other ${left} seats`;
  switch (r.status) {
    case 'projected':
    case 'decided': {
      const o = r.projected_winners.filter((id) => !ap.includes(id));
      if (!o.length || o.length > left) return '';
      return `${r.status === 'decided' ? 'Certain' : 'Projected'} to win ${seats}: ${nameList(o)}.`;
    }
    case 'leaning': {
      const sec = secureSeats(r).filter((c) => !ap.includes(c.choice_id)).slice(0, left).map((c) => c.choice_id);
      const open = left - sec.length;
      if (!sec.length) return `${seats.replace(/^t/, 'T')} ${left === 1 ? 'is' : 'are'} too close to call.`;
      if (open <= 0) return `Likely to win ${seats}: ${nameList(sec)}.`;
      return `${nameList(sec)} ${sec.length > 1 ? 'are' : 'is'} likely to win a seat; the last ${open === 1 ? 'seat is' : `${open} seats are`} too close to call.`;
    }
    case 'counting':
    case 'not_reporting': return `Too early to call ${seats}.`;
    default: return '';
  }
}

/** Race whose Charleston precinct data anchors the home page: Governor, then President, Senate, any statewide. */
export function anchorRace<R extends Pick<RaceSummary, 'race_id' | 'group' | 'scope'>>(races: R[], scope: 'charleston' | 'statewide' = 'charleston'): R | null {
  const pre = scope === 'charleston' ? 'chs' : 'sc';
  for (const k of ['gov', 'pres', 'ussen']) {
    const r = races.find((x) => x.race_id === `${pre}-${k}`);
    if (r) return r;
  }
  return races.find((x) => x.group === 'statewide' && (scope === 'charleston' ? x.scope === 'charleston' : x.scope === 'statewide')) ?? null;
}

/** chs-X <-> sc-X counterpart id. */
export function counterpartId(id: string): string | null {
  const m = /^(chs|sc)-(.+)$/.exec(id);
  return m ? `${m[1] === 'chs' ? 'sc' : 'chs'}-${m[2]}` : null;
}

/** Write-in line (by name: referendum Yes/No choices also carry party 'NON'). */
export const isWriteIn = (c: Pick<ChoiceSummary, 'name' | 'party'>) => /^write[- ]?ins?$/i.test(c.name.trim());

/** "A", "A and B", "A, B and C". */
const lf = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }); // AP style: no Oxford comma
export const listText = (xs: string[]) => lf.format(xs);

/** Vote-for-N: candidates with at least a 90% chance of winning a seat, most likely first. */
export function secureSeats(r: Pick<RaceSummary, 'choices' | 'p_win'>): ChoiceSummary[] {
  if (!r.p_win) return [];
  return r.choices.filter((c) => (r.p_win![c.choice_id] ?? 0) >= 0.9).sort((a, b) => (r.p_win![b.choice_id] ?? 0) - (r.p_win![a.choice_id] ?? 0));
}

/** Short scope label for toggles/kickers: Charleston (County) · Statewide · Full district. */
export function scopeLabel(r: Pick<RaceSummary, 'scope' | 'group'>, long = false): string {
  if (r.scope === 'charleston') return long ? 'Charleston County' : 'Charleston';
  return r.group === 'congress' || r.scope === 'multi_county' ? 'Full district' : 'Statewide';
}

/** Election year of a dataset (replay year, else the election date/name). */
export function electionYear(m: Pick<Meta, 'election' | 'dataset'> | null | undefined): number | null {
  if (!m) return null;
  if (m.dataset?.year) return m.dataset.year;
  const y = (m.election.date.match(/\d{4}/) ?? m.election.name.match(/\d{4}/))?.[0];
  return y ? Number(y) : null;
}

/** Before any results: no snapshot yet, or every race in view has zero votes counted. */
export function isPreResults(m: Pick<Meta, 'snapshot_id'> | null | undefined, races: Pick<RaceSummary, 'choices'>[] | null | undefined): boolean {
  if (m?.snapshot_id === null) return true;
  return !!races && races.every((r) => r.choices.every((c) => c.votes === 0));
}

/** Votes left to count (estimate): projected total minus counted, else the sum of per-vote-type remaining. */
export function leftToCount(r: Pick<RaceFile, 'total_votes' | 'by_type'>): { median: number; lo: number | null; hi: number | null } | null {
  const p = r.total_votes.projected, c = r.total_votes.counted;
  if (p) return { median: Math.max(0, p.median - c), lo: Math.max(0, p.lo90 - c), hi: Math.max(0, p.hi90 - c) };
  const xs = r.by_type.filter((t) => t.remaining);
  if (!xs.length) return null;
  return { median: xs.reduce((a, t) => a + t.remaining!.median, 0), lo: xs.reduce((a, t) => a + t.remaining!.lo90, 0), hi: xs.reduce((a, t) => a + t.remaining!.hi90, 0) };
}

/** Choices ranked by their counted votes in one unit (precinct/county). */
export function rankIn<C extends Pick<ChoiceSummary, 'choice_id'>>(choices: C[], u: Pick<Unit, 'votes'>): C[] {
  return [...choices].sort((a, b) => (u.votes[b.choice_id] ?? 0) - (u.votes[a.choice_id] ?? 0));
}

/** Display name of a unit; pseudo units (countywide provisional/failsafe) get a plain label. */
export const unitName = (u: Pick<Unit, 'unit_id' | 'name'>) => (u.unit_id.includes('*') ? 'Provisional & failsafe (countywide)' : u.name);

/** Any votes counted at all in this race? */
export const hasVotes = (r: Pick<RaceSummary, 'choices'>) => r.choices.some((c) => c.votes > 0);
